import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getPageItems, receivedTags } from './actions'
import { makeSelectorById, makeSelectorTag } from './selectors'
import { makeSelectMetadataByRequestId, selectStatusByRequestId } from '../busy/selectors'
import { busy, notBusy } from '../busy/reducer'
import api from '../api'
import { handleAddErrors, handleDeleteErrors, handleGetErrors, handleUpdateErrors } from '../error/actions'
import { useRequestId } from '../../hooks/useRequestId'

export function useGetTagByIdQuery (id) {
  const reqId = useRequestId([id])
  const dispatch = useDispatch()
  const [refetching, setRefetching] = useState(false)
  const selectEmployeeById = useMemo(makeSelectorById, [])

  const status = useSelector((state) => selectStatusByRequestId(state, reqId))
  const data = useSelector((state) => selectEmployeeById(state, id))

  const effects = useCallback((requestId, params) => {
    dispatch(busy({ requestId }))
    api.getTags(params).then(result => {
      dispatch(receivedTags(result.data.value))
      dispatch(notBusy({ requestId }))
    }).catch(handleGetErrors('tag', dispatch, requestId))
  }, [dispatch])

  useEffect(() => {
    if ((status === undefined || refetching) && id) {
      effects(reqId, { query: [{ key: 'id', op: '=', value: id }] })
      setRefetching(false)
    }
  }, [effects, id, reqId, status, refetching, setRefetching])

  const refetch = useCallback(() => {
    setRefetching(true)
  }, [setRefetching])

  const isLoading = status === 'pending' || status === undefined
  const isError = status === 'rejected'
  const isSuccessful = status === 'resolved'
  const isUnitialized = status === undefined
  return { isUnitialized, isError, isSuccessful, data, refetch, isLoading }
}

export function useGetTagQuery (params = { limit: 50 }) {
  const reqId = useRequestId()
  const dispatch = useDispatch()
  const selectEmployees = useMemo(makeSelectorTag, [])

  const status = useSelector((state) => selectStatusByRequestId(state, reqId))
  const data = useSelector(selectEmployees)

  const effects = useCallback((requestId, params) => {
    dispatch(busy({ requestId }))
    api.getTags(params).then(result => {
      dispatch(receivedTags(result.data.value))
      dispatch(notBusy({ requestId }))
    }).catch(handleGetErrors('tag', dispatch, requestId))
  }, [dispatch])

  useEffect(() => {
    if (status === undefined) {
      effects(reqId, params)
    }
  }, [effects, params, reqId, status])

  const refetch = useCallback((params) => {
    effects(reqId, params)
  }, [reqId])

  const isLoading = status === 'pending' || status === undefined
  const isError = status === 'rejected'
  const isSuccessful = status === 'resolved'
  const isUnitialized = status === undefined
  return { isUnitialized, isError, isSuccessful, data, refetch, isLoading }
}

export function useAddTagMutation () {
  const requestId = useRequestId()
  const dispatch = useDispatch()
  const selectMetadataByRequestId = useMemo(makeSelectMetadataByRequestId, [])

  const metadata = useSelector((state) => selectMetadataByRequestId(state, requestId))
  const status = useSelector((state) => selectStatusByRequestId(state, requestId))

  const mutation = useCallback((tag) => {
    dispatch(busy({ requestId }))
    api.addTag(tag).then(result => {
      dispatch(notBusy({ requestId, id: result.data.value?.id }))
    }).catch(handleAddErrors('tag', dispatch))
  }, [dispatch, requestId])

  const isUnitialized = status === 'pending' || status === undefined
  const isLoading = status === 'pending'
  const isSuccessful = status === 'resolved'
  const isError = status === 'rejected'

  return { metadata, mutation, isLoading, isError, isUnitialized, isSuccessful }
}

export function useUpdateTagMutation () {
  const requestId = useRequestId()
  const dispatch = useDispatch()

  const selectMetadataByRequestId = useMemo(makeSelectMetadataByRequestId, [])
  const metadata = useSelector((state) => selectMetadataByRequestId(state, requestId))
  const status = useSelector((state) => selectStatusByRequestId(state, requestId))

  const mutation = useCallback((tag) => {
    dispatch(busy({ requestId }))
    api.updateTag(tag).then(result => {
      dispatch(notBusy({ requestId, id: result.data.value?.id }))
    }).catch(handleUpdateErrors('tag', dispatch, requestId))
  }, [dispatch, requestId])

  const isUnitialized = status === undefined
  const isLoading = status === 'pending'
  const isSuccessful = status === 'resolved'
  const isError = status === 'rejected'

  return { metadata, mutation, isLoading, isError, isUnitialized, isSuccessful }
}

export function useDeleteTagMutation () {
  const requestId = useRequestId()
  const dispatch = useDispatch()

  const selectMetadataByRequestId = useMemo(makeSelectMetadataByRequestId, [])
  const metadata = useSelector((state) => selectMetadataByRequestId(state, requestId))
  const status = useSelector((state) => selectStatusByRequestId(state, requestId))

  const mutation = useCallback((tag) => {
    dispatch(busy({ id: tag.id, requestId }))
    api.deleteTag(tag).then(() => {
      dispatch(notBusy({ requestId }))
      dispatch(getPageItems())
    }).catch(handleDeleteErrors('tag', dispatch, requestId))
  }, [dispatch, requestId])

  const isUnitialized = status === undefined
  const isLoading = status === 'pending'
  const isSuccessful = status === 'resolved'
  const isError = status === 'rejected'

  return { metadata, mutation, isLoading, isError, isUnitialized, isSuccessful }
}
